<template>
  <div>
    <v-stepper v-model="step" vertical flat>
      <v-stepper-step :complete="step > 1" step="1" :editable="step > 1">
        選擇套版（單選）
      </v-stepper-step>

      <v-stepper-content step="1">
        <v-sheet class="mb-5 px-5 overflow-y-auto" outlined rounded="lg" max-height="400">
          <v-radio-group v-model="messageType">
            <v-row>
              <v-col
                v-for="item in messageTypes"
                :key="item.value"
                cols="12"
                md="6"
                lg="4"
              >
                <v-radio :label="item.label" :value="item.value"/>
                <message-card
                  :type="item.value"
                  :selected="messageType === item.value"
                  @select="messageType = item.value"
                />
              </v-col>
            </v-row>
          </v-radio-group>
        </v-sheet>
        <div class="mt-5">
          <v-btn color="primary" @click="step = 2" :disabled="!messageType">
            下一步
          </v-btn>
        </div>
      </v-stepper-content>

      <v-stepper-step :complete="step > 2" step="2" :editable="step > 2">
        選擇發送管道
      </v-stepper-step>

      <v-stepper-content step="2">
        <v-sheet class="mb-5 pa-5" outlined rounded="lg">
          <v-checkbox v-model="channels" label="LINE訊息 (已綁定LINE的患者)" value="line" hide-details="auto" dense
                      style="width: fit-content;"/>
          <v-checkbox v-model="channels" label="簡訊 (未綁定LINE的患者)" value="sms-unbound" hide-details="auto" dense
                      style="width: fit-content;"/>
          <v-checkbox v-model="channels" label="簡訊 (已綁定LINE的患者)" value="sms-bound" hide-details="auto" dense
                      style="width: fit-content;"/>
        </v-sheet>
        <v-btn color="primary" @click="step = 3" :disabled="channels.length === 0">
          下一步
        </v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 3" step="3" :editable="step > 3">
        選擇指定的約診日期
      </v-stepper-step>

      <v-stepper-content step="3">
        <v-sheet class="mb-5 pa-5" outlined rounded="lg">
          <v-row>
            <v-col cols="12">
              <v-menu
                ref="dateMenu"
                v-model="dateMenu"
                :close-on-content-click="false"
                :return-value.sync="dates"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="dateRangeText"
                    label="看診日期* (可選區間)"
                    prepend-icon="mdi-calendar"
                    readonly
                    clearable
                    hide-details
                    :rules="[v => !!v || '此欄位為必填']"
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="dates"
                  no-title
                  scrollable
                  range
                  locale="zh"
                  :allowed-dates="allowedDates"
                >
                  <v-spacer></v-spacer>
                  <v-btn text color="primary" @click="dateMenu = false">
                    取消
                  </v-btn>
                  <v-btn text color="primary" @click="$refs.dateMenu.save(dates)">
                    確認
                  </v-btn>
                </v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <SelectWithSelectAll
                v-if="clinicUser"
                v-model="selectedDoctors"
                :items="doctors"
                label="約診醫師"
                item-text="doctor_name"
                item-value="doctor_code"
              />
              <SelectWithSelectAll
                v-else
                v-model="selectedClinics"
                :items="clinics"
                label="診所"
                item-text="clinic_name"
                item-value="clinic_code"
              />
            </v-col>
            <v-col cols="12" sm="6">
              <select-with-select-all
                v-model="selectedTag"
                :items="reserveTags"
                label="約診標籤"
                item-text="content"
                item-value="tag_id"
              />
            </v-col>
          </v-row>
        </v-sheet>
        <v-btn color="primary" @click="goToStep4" :disabled="dates.length === 0" :loading="loadingAppointments">
          下一步
        </v-btn>
        <v-btn outlined class="ml-2" color="grey-7" @click="clearStep3">
          清除篩選
        </v-btn>
      </v-stepper-content>

      <v-stepper-step :complete="step > 4" step="4" :editable="step > 4">
        選擇發送約診
      </v-stepper-step>

      <v-stepper-content step="4">
        <v-row class="d-flex" style="min-height: 450px">
          <v-col cols="12" md="7" class="d-flex">
            <v-sheet class="mb-5 px-5 d-flex flex-column flex-grow-1" outlined rounded="lg">
              <v-data-table
                v-model="selectedAppointments"
                :headers="appointmentHeaders"
                :items="appointments"
                :search="appointmentSearch"
                item-key="id"
                show-select
                class="flex-grow-1"
                height="400"
                fixed-header
                hide-default-footer
                :items-per-page="-1"
              >
                <template v-slot:top>
                  <v-text-field
                    v-model="appointmentSearch"
                    label="搜尋病患姓名..."
                    class="mx-4"
                  ></v-text-field>
                </template>
              </v-data-table>
            </v-sheet>
          </v-col>
          <v-col cols="12" md="5" class="d-flex">
            <v-sheet class="mb-5 pa-3 d-flex flex-column flex-grow-1" outlined rounded="lg">
              <div class="d-flex align-center mb-2">
                <span class="text-subtitle-1">已選約診 ({{ selectedAppointments.length }})</span>
                <v-spacer></v-spacer>
                <v-btn small text color="error" @click="selectedAppointments = []"
                       :disabled="selectedAppointments.length === 0">
                  全部清除
                </v-btn>
              </div>
              <v-divider/>
              <v-list style="overflow-y: auto;" class="pt-0 flex-grow-1 overflow-y-auto" max-height="468">
                <v-list-item v-for="item in selectedAppointments" :key="item.id">
                  <v-list-item-content>
                    <v-list-item-title>{{ item.patientName }}</v-list-item-title>
                    <v-list-item-subtitle>{{ item.datetime }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn icon small @click="removeAppointment(item)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-list-item v-if="selectedAppointments.length === 0">
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-center">尚無選擇</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-sheet>
          </v-col>
        </v-row>
        <v-btn color="primary" @click="step = 5" :disabled="selectedAppointments.length === 0">
          下一步
        </v-btn>
      </v-stepper-content>

      <v-stepper-step step="5">
        確認發送
      </v-stepper-step>
      <v-stepper-content step="5">
        <v-sheet class="mb-5 pa-5" outlined rounded="lg">
          <p><strong>發送人數：</strong> {{ summaryRecipients }}</p>
          <p><strong>發送內容：</strong> {{ summaryMessageType }}</p>
        </v-sheet>
        <v-btn color="primary" @click="beforeSend" :loading="loadingQuota">
          確定, 我要送出訊息
        </v-btn>
      </v-stepper-content>
    </v-stepper>

    <ConfirmDialog
      v-model="quotaDialog"
      title="訊息餘額"
      confirm-btn-text="確定發送"
      confirm-btn-color="primary"
      cancel-btn-text="取消"
      :loading="loadingSend"
      @confirm="sendMessage"
    >
      <template v-slot:content>
        <v-card-text>
          <div class="text-pre-line text-center">
            <div style="font-size: 16px;">
              <template v-if="usedLineChannel">
                目前剩餘LINE訊息約：<span class="text-decoration-underline">{{ lineQuota }}</span> 則<br>
                本次預計使用：<span class="text-decoration-underline">{{ useLineQuota }}</span> 則<br>
              </template>
              <template v-if="usedSmsChannel">
                目前剩餘簡訊額度: <span class="text-decoration-underline">{{ smsQuota }}</span> 點<br>
                本次預計使用： <span class="text-decoration-underline">{{ useSmsQuota }} ~ {{ useSmsQuota * 2 }}</span> 點
              </template>
            </div>
            <div class="mt-4">
              <template v-if="usedLineChannel">
                建議您前往 <a href="https://manager.line.biz/" target="_blank" style="color: #0044FF;">LINE後台</a> ，確認訊息量是否足夠與加購<br>
              </template>
              <template v-if="usedSmsChannel">
                前往左側 <router-link :to="{name: 'smsRefillLog'}" style="color: #0044FF;">點數儲值</router-link> ，確認簡訊點數是否足夠與加購<br>
              </template>
              若不足但選擇發出，將只有部分患者收到唷！
            </div>
          </div>
        </v-card-text>
      </template>
    </ConfirmDialog>

    <ConfirmDialog
      v-model="successDialog"
      title="發送成功"
      :content="'預計發送完畢時間： 10 分鐘後\n10 分鐘後可至發送紀錄瀏覽'"
      confirm-btn-text="我知道了，關閉視窗"
      confirm-btn-color="grey-5"
      cancel-btn-text=""
      @confirm="successDialog = false"
    />

    <ConfirmDialog
      v-model="errorDialog"
      :title="errorTitle"
      :content="errorMessage"
      confirm-btn-text="我知道了，關閉視窗"
      confirm-btn-color="grey-5"
      cancel-btn-text="聯繫客服"
      cancel-btn-color="primary"
      @confirm="errorDialog = false"
      @cancel="contactCustomerService"
    />
  </div>
</template>

<script>
import MessageCard from '@/components/message/ManualAppointmentNotification/MessageCard.vue'
import SelectWithSelectAll from '@/components/message/ManualAppointmentNotification/SelectWithSelectAll.vue'
import { mapGetters, mapMutations } from 'vuex'
import {
  getDoctorListAPI,
  getClinicListAPI,
  getReserveTagAPI,
  getMessageSettingAPI,
  getMessagePermissionAPI,
  getSmsQuotaAPI,
  getChannelQuotaAPI,
  getManualSendAppointmentsAPI,
  sendManualMessagesAPI
} from '@/api'
import ConfirmDialog from "@/components/utils/ConfirmDialog.vue";

export default {
  name: 'ManualAppointmentNotification',
  components: {
    ConfirmDialog,
    MessageCard,
    SelectWithSelectAll
  },
  data() {
    return {
      step: 1,
      messageType: null,
      messageTypes: [
        {label: '看診提醒', value: 'reminder'},
        {label: '評價邀請', value: 'review'},
        {label: '術後關懷', value: 'care'}
      ],
      channelMap: {
        'line': 'LINE訊息 (已綁定LINE的患者)',
        'sms-bound': '簡訊 (已綁定LINE的患者)',
        'sms-unbound': '簡訊 (未綁定LINE的患者)'
      },
      channels: [],
      // Step 3
      dateMenu: false,
      dates: [],
      doctors: [],
      selectedDoctors: [],
      selectedClinics: [],
      clinics: [],
      tags: [],
      selectedTag: [],
      reserveTags: [],
      // Step 4
      appointmentSearch: '',
      appointmentHeaders: [
        {text: '約診日期時間', value: 'datetime'},
        {text: '病患姓名', value: 'patientName'},
        {text: '年齡', value: 'age'},
        {text: '性別', value: 'gender'}
      ],
      appointments: [
        {id: 1, datetime: '2025-07-01 13:00', patientName: '丹小科', age: 18, gender: '女性', bindStatus: 0},
        {id: 2, datetime: '2025-07-01 14:00', patientName: '王大明', age: 35, gender: '男性', bindStatus: 1},
        {id: 3, datetime: '2025-07-01 15:00', patientName: '陳小姐', age: 28, gender: '女性', bindStatus: 1}
      ],
      selectedAppointments: [],

      quotaDialog: false,
      lineQuota: 0,
      useLineQuota: 0,
      smsQuota: 0,
      useSmsQuota: 0,

      successDialog: false,
      errorDialog: false,
      errorTitle: '',
      errorMessage: '',

      loadingAppointments: false,
      loadingQuota: false,
      loadingSend: false
    }
  },
  computed: {
    ...mapGetters([
      'axiosParamData',
      'clinicUser',
      'ownChannelLoaded',
      'hasOwnChannel'
    ]),
    dateRangeText: {
      get: function () {
        return this.dates.join(' ~ ')
      },
      set: function () {
        this.dates = []
      }
    },
    allowedDates() {
      // if (this.dates.length === 1) {
      //   const firstDate = new Date(this.dates[0]);
      //   const minDate = new Date(firstDate);
      //   minDate.setDate(firstDate.getDate() - 2);
      //   const maxDate = new Date(firstDate);
      //   maxDate.setDate(firstDate.getDate() + 2);
      //
      //   return date => {
      //     const currentDate = new Date(date);
      //     return currentDate >= minDate && currentDate <= maxDate;
      //   };
      // }
      return () => true;
    },
    summaryRecipients() {
      const tmp = []

      if (this.usedLineChannel) {
        tmp.push(`Line ${this.sendLineCount} 人`)
      }

      if (this.usedSmsChannel) {
        tmp.push(`簡訊 ${this.sendSmsCount} 人`)
      }

      return tmp.join(', ')
    },
    summaryMessageType() {
      const mapping = {
        'reminder': '看診提醒',
        'review': '評價邀請',
        'care': '術後關懷'
      }
      return mapping[this.messageType] || '未選擇'
    },
    usedLineChannel() {
      return this.channels.includes('line')
    },
    usedSmsChannel() {
      return ['sms-bound', 'sms-unbound'].some(v => this.channels.includes(v))
    },
    sendSmsCount() {
      let count = 0
      if (this.channels.includes('sms-bound')) {
        const tmp = this.selectedAppointments.filter(e => !!e.bindStatus)
        count += tmp.length
      }
      if (this.channels.includes('sms-unbound')) {
        const tmp = this.selectedAppointments.filter(e => !e.bindStatus)
        count += tmp.length
      }
      return count
    },
    sendLineCount() {
      if (this.usedLineChannel) {
        const bindAppointments = this.selectedAppointments.filter(e => !!e.bindStatus)
        return bindAppointments.length
      }
    }
  },
  methods: {
    ...mapMutations({
      setOwnChannel: 'setOwnChannel'
    }),
    removeAppointment(item) {
      const index = this.selectedAppointments.findIndex(a => a.id === item.id);
      if (index > -1) {
        this.selectedAppointments.splice(index, 1);
      }
    },
    clearStep3() {
      this.dates = []
      this.selectedDoctors = []
      this.selectedClinics = []
      this.selectedTag = []
    },
    async goToStep4() {
      this.loadingAppointments = true
      try {
        const params = {
          ...this.axiosParamData,
          startDate: this.dates[0],
          endDate: this.dates.length === 2 ? this.dates[1] : this.dates[0]
        }

        if (this.clinicUser && this.selectedDoctors.length > 0) {
          params.doctorIds = this.selectedDoctors.join(',')
        }

        if (!this.clinicUser && this.selectedClinics.length > 0) {
          params.clinicIds = this.selectedClinics.join(',')
        }

        if (this.selectedTag.length > 0) {
          params.tagIds = this.selectedTag.join(',')
        }

        const res = await getManualSendAppointmentsAPI(params)
        this.appointments = res.data
        this.step = 4
      } catch (error) {
        console.error('Failed to fetch appointments:', error)
        this.errorTitle = '取得約診列表失敗'
        this.errorMessage = error.response?.data?.message || '取得約診列表時發生錯誤，請稍後再試'
        this.errorDialog = true
      } finally {
        this.loadingAppointments = false
      }
    },
    async getSmsQuota() {
      const res = await getSmsQuotaAPI(this.axiosParamData)
      this.smsQuota = res.data.quota
    },
    async getChannelQuota() {
      const res = await getChannelQuotaAPI(this.axiosParamData)
      this.lineQuota = Number(res.data.quota) - Number(res.data.total_usage)
    },
    async beforeSend() {
      this.loadingQuota = true
      try {
        if (this.usedLineChannel) {
          await this.getChannelQuota()
          this.useLineQuota = this.sendLineCount
        }

        if (this.usedSmsChannel) {
          await this.getSmsQuota()
          this.useSmsQuota = this.sendSmsCount
        }

        this.quotaDialog = true
      } finally {
        this.loadingQuota = false
      }
    },
    async sendMessage() {
      this.loadingSend = true
      try {
        const data = {
          ...this.axiosParamData,
          messageType: this.messageType,
          channels: this.channels,
          appointmentIds: this.selectedAppointments.map(a => a.id)
        }

        const res = await sendManualMessagesAPI(data)

        if (res.data.status === 'success') {
          this.quotaDialog = false
          this.successDialog = true
          this.step = 1
          this.selectedAppointments = []
        } else {
          throw new Error(res.data.message || '發送失敗')
        }
      } catch (error) {
        console.error('Failed to send messages:', error)
        this.quotaDialog = false
        this.errorTitle = '發送失敗'
        this.errorMessage = error.response?.data?.messages || error.response?.data?.message || error.message || '發送訊息時發生錯誤，請稍後再試'
        this.errorDialog = true
      } finally {
        this.loadingSend = false
      }
    },
    contactCustomerService() {
      window.open(this.$enum.DENTCO_CS_LINE, '_blank')
      this.errorDialog = false
    },
    async checkChannel() {
      if (!this.ownChannelLoaded) {
        const res = await getMessageSettingAPI(this.axiosParamData)
        this.setOwnChannel(res.data.own_channel)
      }
      if (!this.hasOwnChannel) {
        const res = getMessagePermissionAPI(this.axiosParamData)
        this.restrict = Boolean(Number(res.data.send_restriction))
      }
    }
  },
  mounted() {
    if (this.clinicUser) {
      getDoctorListAPI(this.axiosParamData).then(res => {
        this.doctors = res.data
      })
    } else {
      getClinicListAPI(this.axiosParamData).then(res => {
        this.clinics = res.data
      })
    }
    getReserveTagAPI(this.axiosParamData).then(res => {
      this.reserveTags = res.data.filter(tag => !!tag.content)
    })
    this.checkChannel()
  },
  watch: {
    dates(newDates) {
      if (newDates.length === 2) {
        const [start, end] = newDates
        if (start > end) {
          this.dates = [end, start]
        }
      }
    }
  }
}
</script>